"use client";

import React, { useEffect } from "react";
import {
  Button,
  Typography,
  Box,
  Alert,
  Grid,
  Card,
  CardActionArea,
  CardContent,
} from "@mui/material";
import WorkIcon from "@mui/icons-material/Work";
import SchoolIcon from "@mui/icons-material/School";
import DescriptionIcon from "@mui/icons-material/Description";
import SearchIcon from "@mui/icons-material/Search";
import CreateIcon from "@mui/icons-material/Create";

type ActionLink = {
  label?: string;
  href?: string;
  variant?: "contained" | "outlined";
};

type Props = {
  itemName?: string; // e.g. "Resume", "Referral"
  action?: ActionLink;
  showRecommendations?: boolean;
};

const defaultRecommendations = [
  {
    title: "Build Your Resume",
    description: "Create a professional resume with our AI-powered builder",
    icon: CreateIcon,
    href: "/resume-builder",
    color: "primary.main",
  },
  {
    title: "Track Applications",
    description: "Manage and monitor your job applications",
    icon: WorkIcon,
    href: "/jobtracker",
    color: "primary.main",
  },
  {
    title: "Write Cover Letters",
    description: "Create compelling cover letters and motivation letters",
    icon: DescriptionIcon,
    href: "/motivation-letter-creator",
    color: "primary.main",
  },
  {
    title: "Explore Job Insights",
    description: "See What Others Are Applying To",
    icon: SearchIcon,
    href: "/jobs",
    color: "primary.main",
  },
  {
    title: "Check Companies Hiring",
    description: "See which companies are hiring and get hired",
    icon: SearchIcon,
    href: "/company-status",
    color: "primary.main",
  },
];

export default function InPageNotFound({
  itemName = "Item",
  action,
  showRecommendations = true,
}: Props) {
  const actionLabel = action?.label;
  const actionHref = action?.href;
  const actionVariant = action?.variant ?? "contained";

  // Add noindex meta tag to prevent search engine indexing
  useEffect(() => {
    const metaRobots = document.createElement("meta");
    metaRobots.name = "robots";
    metaRobots.content = "noindex, nofollow";
    document.head.appendChild(metaRobots);

    return () => {
      // Cleanup: remove the meta tag when component unmounts
      document.head.removeChild(metaRobots);
    };
  }, []);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="60vh"
      textAlign="center"
      gap={2}
      sx={{ p: 3 }}
    >
      <Typography variant="h1" color="text.secondary" fontWeight="bold">
        404
      </Typography>

      <Alert severity="error" sx={{ mb: 2, width: "100%", maxWidth: 700 }}>
        <Typography variant="h6" fontWeight="bold">
          {itemName} Not Found
        </Typography>
        <Typography variant="body1">
          The {itemName.toLowerCase()} page you are looking for is not
          accessible or not available. Please check the link or try again
          later.
        </Typography>
      </Alert>

      <Box sx={{ display: "flex", gap: 2, mt: 1, mb: 2 }}>
        <Button
          variant="outlined"
          onClick={() => window.history.back()}
          startIcon={<CreateIcon />}
        >
          Go Back
        </Button>

        {actionHref && (
          <Button variant={actionVariant} href={actionHref}>
            {actionLabel ?? `Open ${itemName}`}
          </Button>
        )}
      </Box>

      {showRecommendations && (
        <>
          <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
            Here are some helpful recommendations:
          </Typography>

          <Grid container spacing={2} maxWidth="lg">
            {defaultRecommendations.map((item, index) => {
              const IconComponent = item.icon as any;
              return (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card>
                    <CardActionArea href={item.href}>
                      <CardContent sx={{ textAlign: "center", p: 2 }}>
                        <IconComponent sx={{ fontSize: 36, color: item.color, mb: 1 }} />
                        <Typography variant="h6" gutterBottom>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.description}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}
    </Box>
  );
}
